import type { FC } from 'react';
import { SynchronizationWrapper } from './Synchronization.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface SynchronizationProps { }

const Synchronization: FC<SynchronizationProps> = () => (
   <SynchronizationWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Synchronization</h1>
      </header>
      <div className="entry-content clear" itemProp="text"  style={{ height: 'auto !important' }}>
         <p className="text-align-justify">Synchronization is a feature in java that controls the access of multiple threads to any shared resource. It is useful where we want to allow only one thread to access the shared resource. Alos, to prevent consistency problems and thread interference.</p>
         <p className="text-align-justify">Implementation of Synchronization in Java is done with a concept called monitors. The monitor can be accessed by only one thread at a given time. When a thread enters the monitor, it acquires a lock on that monitor. Until the first thread exits the monitor, all other threads attempting to enter the locked monitor will be suspended until the first thread exits the monitor.</p>
         <br />
         <h4 className="text-align-justify"><strong>Deadlock </strong></h4>
         <p className="text-align-justify">Deadlock in java is a part of multi-threading. It can occur in a situation when a thread is waiting for an object lock, that is acquired by another thread, and second thread is waiting for an object lock that is acquired by the first thread. Deadlock is a condition when both threads are waiting for each other to release the lock.</p>
         <br />
         <h4 className="text-align-justify"><strong>There are two types of thread synchronization </strong></h4>
         <br />
         <h4 className="text-align-justify">1. <strong>Mutual Exclusive</strong> </h4>
         <p className="text-align-justify">It helps keep threads from interfering with one another while sharing data. Following are the options to achieve the same:</p>
         <ul className="text-align-justify">
            <li><strong>Synchronized method </strong>
               <ul>
                  <li>This method locks an object for any shared resource.</li>
                  <li>When a synchronized method is invoked by a thread, it automatically acquires the lock for that object and releases it when the thread completes its task.</li>
                  <li>Syntax :
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'synchronized void print(object reference expression) {\n'+
                           '  //Access shared variables and other shared resources\n'+
                           '}'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
            <li><strong>Synchronized block </strong>
               <ul>
                  <li>It helps us perform synchronization on any specific resource of the method.</li>
                  <li>Suppose you have 10 lines of code in your method, and you want to synchronize only 5 lines, you can use a synchronized block.</li>
                  <li>Syntax :
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'synchronized(object reference expression) {\n'+
                           '  //code block\n'+
                           '}'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
            <li><strong>Static synchronization </strong>
               <ul>
                  <li>If we make any static method as synchronized, the lock will be on the class not on the object.</li>
                  <li>There will be no interference between objects of two classes.</li>
                  <li>Syntax :
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'synchronized static void print(object reference expression) {\n'+
                           '  //Access shared variables and other shared resources\n'+
                           '}'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
         </ul>
         <br />
         <h4 className="text-align-justify">2. <strong>Cooperation </strong></h4>
         <ul className="text-align-justify">
            <li>It is an inter-thread communication.</li>
            <li>It allows synchronized threads to communicate with each other.</li>
            <li>Cooperation is a condition where a thread’s execution is paused that is running in its critical section and another thread is allowed to enter (or lock) in the same critical section to be executed.</li>
            <li>We can implement it with the help of the following methods of&nbsp;Object class:
               <ul>
                  <li><strong>wait() </strong>
                     <ul>
                        <li>It causes the current thread to release the lock and wait until either a specified amount of time has elapsed or another thread notifies the object by invoking notify() or notifyAll().</li>
                        <li>Eg :
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'public final void wait()\n'+
                                 'public final void wait(long timeout)'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <li><strong>notify()</strong>
                     <ul>
                        <li>It wakes up one of the arbitrarily chosen thread that is waiting on this object’s monitor.</li>
                        <li>Eg :
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                           {
                              'public final void notify()'
                           }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <li><strong>notifyAll() </strong>
                     <ul>
                        <li>It will wake up all the threads that have been waiting on this object’s monitor.</li>
                        <li>Eg :
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'public final void notifyAll()'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
               </ul>
            </li>
         </ul>
      </div>
   </SynchronizationWrapper>
);

export default Synchronization;
